/**
 * Pure style helpers shared by the property panels, the canvas renderer
 * and the PNG/PDF export pipeline. Keep free of UI code — these define
 * how stored style values translate to CSS, so canvas and export stay
 * pixel-identical.
 */
import { ELEMENT_TYPES, SIZE_PRESETS } from '../schema/documentSchema';

export function canEditStyle(el, key, mode) {
    if (mode === 'admin') return true;
    if (!el) return false;
    if (el.editableByUser === false) return false;
    if (el.type === ELEMENT_TYPES.PLACEHOLDER || el.type === ELEMENT_TYPES.TEXT) {
        const allowed = el.allowedStyles;
        if (!allowed) return true;
        return allowed.includes(key);
    }
    return true;
}

export const DEFAULT_FONT_FAMILY = 'Hind Siliguri';

/**
 * Font stack shared by the editor (CSS) and the export (canvas).
 * Both must resolve the same primary family AND the same fallback, or glyphs
 * missing from the primary font (common in Bengali) render from a different
 * face in each and the export stops matching the design.
 */
export function resolveFontStack(styles = {}) {
    return `"${styles.fontFamily || DEFAULT_FONT_FAMILY}", "Noto Sans Bengali", sans-serif`;
}

export function matchPresetId(canvas) {
    if (canvas?.presetId && SIZE_PRESETS.some((p) => p.id === canvas.presetId)) {
        return canvas.presetId;
    }
    const hit = SIZE_PRESETS.find(
        (p) => p.id !== 'custom' && p.width === canvas?.width && p.height === canvas?.height
    );
    return hit?.id || 'custom';
}

/**
 * Family and weight lists are no longer fixed here — the panel reads them from
 * `../fonts`, which knows the ~1900 Google families and the exact cuts each one
 * ships. A hard-coded 300…800 list offered weights most families do not have,
 * and the browser faked them on canvas while the export drew the real face.
 */

/** Resolve corner radii — unified borderRadius or per-corner overrides */
export function getCornerRadii(styles = {}) {
    const base = Number(styles.borderRadius) || 0;
    return {
        tl: Number(styles.borderTopLeftRadius ?? base) || 0,
        tr: Number(styles.borderTopRightRadius ?? base) || 0,
        br: Number(styles.borderBottomRightRadius ?? base) || 0,
        bl: Number(styles.borderBottomLeftRadius ?? base) || 0,
    };
}

/** CSS border-radius from unified / per-corner values */
export function resolveBorderRadius(styles = {}) {
    const { tl, tr, br, bl } = getCornerRadii(styles);
    if (!tl && !tr && !br && !bl) return 0;
    if (tl === tr && tr === br && br === bl) return tl;
    return `${tl}px ${tr}px ${br}px ${bl}px`;
}

/* ------------------------------------------------------------------ *
 *  Strokes and effects — repeatable lists (Figma "+" any number of them)
 *
 *  `styles.strokes` / `styles.effects` are the model. Elements written before
 *  those existed (and every non-text element, which still uses the single-value
 *  fields) are read through the same resolvers as a one-entry list, so one code
 *  path paints both and nothing has to be migrated on load.
 * ------------------------------------------------------------------ */

/**
 * A text stroke follows the GLYPH outline (Figma calls the same thing "stroke"
 * on a text node), so only the two positions a glyph stroke can actually be
 * painted are offered: centred on the outline, or entirely outside it. "Inside"
 * would need the stroke clipped to the letterform, which CSS can't express — and
 * an option that only worked in the export would be worse than not having it.
 */
export const STROKE_POSITION_OPTIONS = [
    { value: 'outside', label: 'Outside' },
    { value: 'center', label: 'Center' },
];

export const SHADOW_EFFECTS = ['dropShadow', 'innerShadow', 'textShadow'];

const SHADOW_DEFAULT_COLOR = {
    dropShadow: 'rgba(0,0,0,0.25)',
    innerShadow: 'rgba(0,0,0,0.35)',
    textShadow: 'rgba(0,0,0,0.45)',
};

/** `#rgb` / `#rrggbb` / `#rrggbbaa` / `rgb[a](…)` → {r,g,b,a}; null if unparseable. */
export function parseRgba(color) {
    const c = String(color ?? '').trim();
    if (!c || c === 'transparent' || c === 'none') return { r: 0, g: 0, b: 0, a: 0 };
    if (c.startsWith('#')) {
        const hex = c.slice(1);
        if (!/^[0-9a-f]{3,8}$/i.test(hex)) return null;
        const full = hex.length <= 4 ? hex.split('').map((ch) => ch + ch).join('') : hex;
        return {
            r: parseInt(full.slice(0, 2), 16),
            g: parseInt(full.slice(2, 4), 16),
            b: parseInt(full.slice(4, 6), 16),
            a: full.length >= 8 ? parseInt(full.slice(6, 8), 16) / 255 : 1,
        };
    }
    const m = c.match(/rgba?\(([^)]+)\)/i);
    if (!m) return null;
    const p = m[1].split(',').map((v) => parseFloat(v.trim()));
    return { r: p[0], g: p[1], b: p[2], a: p.length > 3 ? p[3] : 1 };
}

const rgbaCss = ({ r, g, b, a }) => `rgba(${Math.round(r)},${Math.round(g)},${Math.round(b)},${Math.round(a * 1000) / 1000})`;

/** Colour at `opacity` percent. */
export function withOpacity(color, opacity = 100) {
    const pct = Math.max(0, Math.min(100, Number(opacity ?? 100))) / 100;
    if (pct >= 1) return color || '#000000';
    const c = parseRgba(color);
    if (!c) return color;
    return rgbaCss({ ...c, a: c.a * pct });
}

/** `src` composited over `dst` (CSS source-over). */
function over(src, dst) {
    const a = src.a + dst.a * (1 - src.a);
    if (a <= 0) return { r: 0, g: 0, b: 0, a: 0 };
    const ch = (s, d) => (s * src.a + d * dst.a * (1 - src.a)) / a;
    return { r: ch(src.r, dst.r), g: ch(src.g, dst.g), b: ch(src.b, dst.b), a };
}

/**
 * Every stroke, hidden ones included — what the panel lists.
 *
 * Read only from the list: a legacy `borderWidth` is a BOX border and now belongs
 * to the Border section, not to the glyph outline this drives. Old `textStroke*`
 * values (the retired "Text outline" effect) do read back as one entry, since
 * that was the same thing under a different name.
 */
export function getStrokeList(styles = {}) {
    if (Array.isArray(styles.strokes)) return styles.strokes.filter(Boolean);
    return (Number(styles.textStrokeWidth) || 0) > 0
        ? [{ id: 'legacy', color: styles.textStrokeColor || '#ffffff' }]
        : [];
}

/** Visible strokes, in paint order (index 0 on top). */
export function getStrokes(styles = {}) {
    return getStrokeList(styles).filter((s) => s.visible !== false);
}

/** Strokes share one weight and one position, exactly like Figma's stroke list. */
export function getStrokeWeight(styles = {}) {
    if (styles.strokeWeight != null) return Math.max(0, Number(styles.strokeWeight) || 0);
    return Math.max(0, Number(styles.textStrokeWidth) || 0);
}
export function getStrokePosition(styles = {}) {
    return styles.strokePosition === 'center' ? 'center' : 'outside';
}

/**
 * The stroke list flattened to ONE colour.
 *
 * Every stroke sits on the same outline at the same weight, so stacking them is
 * pure alpha compositing — and source-over is associative, which means
 * `cN over … over c0 over backdrop` is exactly `(cN over … over c0) over
 * backdrop`. Flattening first is therefore lossless, and it lets a single
 * `-webkit-text-stroke` (and a single canvas stroke) reproduce the whole stack.
 */
export function flattenStrokeColor(styles = {}) {
    const list = getStrokes(styles);
    if (!list.length) return null;
    // List is top-first; composite from the bottom up.
    let acc = { r: 0, g: 0, b: 0, a: 0 };
    [...list].reverse().forEach((s) => {
        const c = parseRgba(withOpacity(s.color || '#000000', s.opacity));
        if (c) acc = over(c, acc);
    });
    return acc.a > 0 ? rgbaCss(acc) : null;
}

/**
 * Glyph-outline stroke for a text element.
 *
 * `-webkit-text-stroke` is always CENTRED on the outline, so "outside" is drawn
 * as a double-weight centred stroke painted *under* the fill — the fill then
 * covers the inner half and what is left is exactly a weight-w outside stroke.
 * The export reproduces the same two cases on canvas.
 */
export function buildTextStroke(styles = {}) {
    const w = getStrokeWeight(styles);
    const color = flattenStrokeColor(styles);
    if (!w || !color) return null;
    const outside = getStrokePosition(styles) === 'outside';
    return {
        width: outside ? w * 2 : w,
        color,
        paintOrder: outside ? 'stroke fill' : undefined,
        // How far the ink reaches past the glyph — for export bleed padding.
        outset: outside ? w : w / 2,
    };
}

/** Every effect of one kind, hidden ones included — what the panel lists. */
export function getEffectList(styles = {}, type) {
    if (Array.isArray(styles.effects)) {
        return styles.effects.filter((e) => e && e.type === type);
    }
    return legacyEffect(styles, type);
}

/** Visible effects of one kind, in paint order. Legacy fields read as one entry. */
export function getEffects(styles = {}, type) {
    return getEffectList(styles, type).filter((e) => e.visible !== false);
}

function legacyEffect(styles, type) {
    // Legacy field prefixes: shadow* / innerShadow* / textShadow*.
    const prefix = type === 'dropShadow' ? 'shadow' : type;
    const n = (k) => Number(styles[`${prefix}${k}`]) || 0;
    const blur = n('Blur');
    const ox = n('OffsetX');
    const oy = n('OffsetY');
    const spread = type === 'textShadow' ? 0 : n('Spread');
    if (!blur && !ox && !oy && !spread) return [];
    return [{
        id: 'legacy',
        type,
        offsetX: ox,
        offsetY: oy,
        blur,
        spread,
        color: styles[`${prefix}Color`] || SHADOW_DEFAULT_COLOR[type],
        opacity: 100,
    }];
}

/** One shadow entry → its CSS colour, opacity folded in. */
export function shadowColorCss(fx, type) {
    return withOpacity(fx.color || SHADOW_DEFAULT_COLOR[type] || 'rgba(0,0,0,0.25)', fx.opacity);
}

/**
 * Build CSS box-shadow — every drop shadow, then every inner shadow.
 */
export function buildBoxShadow(styles = {}) {
    const parts = [];
    getEffects(styles, 'dropShadow').forEach((fx) => {
        parts.push(`${Number(fx.offsetX) || 0}px ${Number(fx.offsetY) || 0}px `
            + `${Number(fx.blur) || 0}px ${Number(fx.spread) || 0}px ${shadowColorCss(fx, 'dropShadow')}`);
    });
    getEffects(styles, 'innerShadow').forEach((fx) => {
        parts.push(`inset ${Number(fx.offsetX) || 0}px ${Number(fx.offsetY) || 0}px `
            + `${Number(fx.blur) || 0}px ${Number(fx.spread) || 0}px ${shadowColorCss(fx, 'innerShadow')}`);
    });
    return parts.length ? parts.join(', ') : undefined;
}

/** Build CSS text-shadow — one entry per text shadow effect. */
export function buildTextShadow(styles = {}) {
    const parts = getEffects(styles, 'textShadow').map((fx) => (
        `${Number(fx.offsetX) || 0}px ${Number(fx.offsetY) || 0}px `
        + `${Number(fx.blur) || 0}px ${shadowColorCss(fx, 'textShadow')}`
    ));
    return parts.length ? parts.join(', ') : undefined;
}

/** Resolve padding CSS from unified or per-side values */
export function resolvePadding(styles = {}) {
    if (
        styles.paddingTop != null
        || styles.paddingRight != null
        || styles.paddingBottom != null
        || styles.paddingLeft != null
    ) {
        const p = Number(styles.padding) || 0;
        return `${styles.paddingTop ?? p}px ${styles.paddingRight ?? p}px ${styles.paddingBottom ?? p}px ${styles.paddingLeft ?? p}px`;
    }
    if (styles.padding != null) return `${styles.padding}px`;
    return undefined;
}

/* ------------------------------------------------------------------ *
 *  Border — the outline around the element BOX.
 *
 *  Most elements keep it in borderWidth / borderColor / borderStyle; shapes
 *  have always called the same thing strokeWidth / stroke / strokeStyle. One
 *  key map lets every renderer, the panel and the export share a single
 *  implementation instead of each type growing its own gradient handling.
 * ------------------------------------------------------------------ */

export const BORDER_KEYS = {
    width: 'borderWidth',
    color: 'borderColor',
    style: 'borderStyle',
    fillType: 'borderFillType',
    prefix: 'border',
    colorDefault: '#000000',
};

export const SHAPE_BORDER_KEYS = {
    width: 'strokeWidth',
    color: 'stroke',
    style: 'strokeStyle',
    fillType: 'strokeFillType',
    prefix: 'stroke',
    colorDefault: '#a1a1aa',
};

/**
 * The GLYPH fill of a text element — the same fill editor as everything else,
 * keyed onto the long-standing `color` style so every existing template keeps
 * its colour and only gains the gradient option.
 */
export const TEXT_FILL_KEYS = {
    color: 'color',
    fillType: 'textFillType',
    prefix: 'text',
    colorDefault: '#18181b',
};

const gradKey = (keys, name) => `${keys.prefix}${name[0].toUpperCase()}${name.slice(1)}`;

export function isGradientText(styles = {}) {
    const t = styles[TEXT_FILL_KEYS.fillType];
    return t === 'linear' || t === 'radial';
}

export function borderWidthOf(styles = {}, keys = BORDER_KEYS) {
    return Math.max(0, Number(styles[keys.width]) || 0);
}

export function isGradientBorder(styles = {}, keys = BORDER_KEYS) {
    const t = styles[keys.fillType];
    return borderWidthOf(styles, keys) > 0 && (t === 'linear' || t === 'radial');
}

/** CSS gradient for the border fill, using the same shape as every other fill. */
export function resolveBorderFill(styles = {}, keys = BORDER_KEYS) {
    return resolveFillBackground({
        backgroundType: styles[keys.fillType],
        backgroundColor: styles[keys.color] || keys.colorDefault,
        gradientColor1: styles[gradKey(keys, 'gradientColor1')],
        gradientColor2: styles[gradKey(keys, 'gradientColor2')],
        gradientAngle: styles[gradKey(keys, 'gradientAngle')],
        gradientCenterX: styles[gradKey(keys, 'gradientCenterX')],
        gradientCenterY: styles[gradKey(keys, 'gradientCenterY')],
    }, 'background');
}

/**
 * The border fill as RAW values instead of a CSS string — for renderers that
 * can't take one, i.e. the SVG stroke on a path shape and the export's canvas
 * gradient. Same keys the CSS resolver above reads.
 */
export function borderFillPaint(styles = {}, keys = BORDER_KEYS) {
    return {
        type: styles[keys.fillType] || 'solid',
        color: styles[keys.color] || keys.colorDefault,
        color1: styles[gradKey(keys, 'gradientColor1')],
        color2: styles[gradKey(keys, 'gradientColor2')],
        angle: styles[gradKey(keys, 'gradientAngle')],
        centerX: styles[gradKey(keys, 'gradientCenterX')],
        centerY: styles[gradKey(keys, 'gradientCenterY')],
    };
}

/** CSS gradient for a text element's glyphs — painted through background-clip: text. */
export function resolveTextFill(styles = {}) {
    return resolveBorderFill(styles, TEXT_FILL_KEYS);
}

/** The same glyph fill as raw values, for the export's canvas gradient. */
export function textFillPaint(styles = {}) {
    return borderFillPaint(styles, TEXT_FILL_KEYS);
}

/* ------------------------------------------------------------------ *
 *  Per-character fill (utils/textRuns `colorSpans`)
 *
 *  A span carries its fill as {color, fill:{type, color1, …}} rather than as
 *  style keys, so these two adapters put it in the shape the shared gradient
 *  code already speaks: a styles-like object for the CSS/canvas gradient
 *  builders, and the flat paint `sampleGradientAt` reads.
 * ------------------------------------------------------------------ */

export function spanFillStyles(paint = null) {
    const g = paint?.fill || null;
    return {
        backgroundType: g?.type || 'solid',
        backgroundColor: paint?.color || TEXT_FILL_KEYS.colorDefault,
        gradientColor1: g?.color1,
        gradientColor2: g?.color2,
        gradientAngle: g?.angle,
        gradientCenterX: g?.centerX,
        gradientCenterY: g?.centerY,
    };
}

/** CSS background for a coloured run — clipped to its glyphs by the renderer. */
export function resolveSpanFill(paint = null) {
    return resolveFillBackground(spanFillStyles(paint), 'background');
}

/** A run's fill as raw values, for the canvas gradient and for glyph sampling. */
export function spanFillPaint(paint = null) {
    const g = paint?.fill || null;
    return {
        type: g?.type || 'solid',
        color: paint?.color || TEXT_FILL_KEYS.colorDefault,
        color1: g?.color1,
        color2: g?.color2,
        angle: g?.angle,
        centerX: g?.centerX,
        centerY: g?.centerY,
    };
}

export function resolveBorder(styles = {}, keys = BORDER_KEYS) {
    const w = borderWidthOf(styles, keys);
    if (!w) return 'none';
    // A gradient border is painted through the background (see buildBorderPaint),
    // so the border itself has to be transparent for the gradient to show.
    if (isGradientBorder(styles, keys)) return `${w}px solid transparent`;
    return `${w}px ${styles[keys.style] || 'solid'} ${styles[keys.color] || keys.colorDefault}`;
}

/**
 * Background shorthand that paints a gradient BORDER as well as the element's
 * own fill: the fill is clipped to the padding box, the border gradient to the
 * border box, and the border itself is transparent. This is the only CSS route
 * to a gradient border that still honours border-radius.
 */
export function buildBorderPaint(styles = {}, elementBackground, keys = BORDER_KEYS) {
    if (!isGradientBorder(styles, keys)) return elementBackground;
    // In a multi-layer `background` shorthand only the LAST layer may be a plain
    // colour — a colour anywhere else makes the whole declaration invalid and the
    // browser drops it, border and fill together. Wrap solids as flat gradients.
    const asImage = (v) => (
        !v || v === 'transparent'
            ? 'linear-gradient(transparent, transparent)'
            : (/^(linear|radial|conic)-gradient|^url\(|^image\(/i.test(v)
                ? v
                : `linear-gradient(${v}, ${v})`)
    );
    return `${asImage(elementBackground)} padding-box, ${asImage(resolveBorderFill(styles, keys))} border-box`;
}

/**
 * A gradient border as its OWN painted ring, for elements whose fill can be
 * see-through. `buildBorderPaint`'s two-layer trick paints the gradient across
 * the whole border box and relies on an opaque fill to cover the middle — a text
 * block or a picture with no background has nothing to cover it with, so the
 * gradient floods the element. This paints only the border band: a box the size
 * of the element, padded by the border width, with its middle masked out.
 *
 * The caller adds `borderRadius` (it owns the corner maths) and positions it on
 * an element box that is itself a containing block.
 */
export function borderRingStyle(styles = {}, keys = BORDER_KEYS) {
    const w = borderWidthOf(styles, keys);
    if (!w || !isGradientBorder(styles, keys)) return null;
    return {
        position: 'absolute',
        inset: 0,
        padding: w,
        background: resolveBorderFill(styles, keys),
        // Paint the whole box, then subtract the content box — what is left is the
        // band the border occupies. Masking THIS div is safe: it has no children.
        WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
        mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
        WebkitMaskComposite: 'xor',
        maskComposite: 'exclude',
        pointerEvents: 'none',
    };
}

/**
 * CSS background for solid / linear / radial fills.
 * @param {'background'|'fill'} kind — uses backgroundColor or fill as solid color
 */
export function resolveFillBackground(styles = {}, kind = 'background') {
    const type = styles.backgroundType || styles.fillType || 'solid';
    const solid = kind === 'fill'
        ? (styles.fill && styles.fill !== 'transparent' ? styles.fill : 'transparent')
        : (styles.backgroundColor && styles.backgroundColor !== 'transparent'
            ? styles.backgroundColor
            : 'transparent');

    if (type === 'solid' || !type) return solid === 'transparent' ? 'transparent' : solid;

    const c1 = styles.gradientColor1 || solid || '#ffffff';
    const c2 = styles.gradientColor2 || '#000000';
    const angle = Number(styles.gradientAngle) || GRADIENT_ANGLE_DEFAULT;
    const cx = styles.gradientCenterX != null ? Number(styles.gradientCenterX) : 50;
    const cy = styles.gradientCenterY != null ? Number(styles.gradientCenterY) : 50;

    if (type === 'radial') {
        return `radial-gradient(circle at ${cx}% ${cy}%, ${c1}, ${c2})`;
    }
    return `linear-gradient(${angle}deg, ${c1}, ${c2})`;
}

/**
 * CSS background for the page (artboard).
 *
 * The page keeps its solid colour in `canvas.background` (not `backgroundColor`
 * like elements), so map that across and reuse the element resolver. Falls back
 * to white — a page is never transparent.
 */
export function resolveCanvasBackground(canvas = {}) {
    const bg = resolveFillBackground({
        backgroundType: canvas.backgroundType,
        backgroundColor: canvas.background || '#ffffff',
        gradientColor1: canvas.gradientColor1,
        gradientColor2: canvas.gradientColor2,
        gradientAngle: canvas.gradientAngle,
        gradientCenterX: canvas.gradientCenterX,
        gradientCenterY: canvas.gradientCenterY,
    }, 'background');
    return bg === 'transparent' ? '#ffffff' : bg;
}

/** CSS filter string for hue / saturation / brightness / etc. */
export function buildCssFilter(styles = {}) {
    const hue = Number(styles.hueRotate) || 0;
    const sat = styles.saturate != null ? Number(styles.saturate) : 100;
    const bri = styles.brightness != null ? Number(styles.brightness) : 100;
    const con = styles.contrast != null ? Number(styles.contrast) : 100;
    const gray = Number(styles.grayscale) || 0;
    const sepia = Number(styles.sepia) || 0;
    const invert = Number(styles.invert) || 0;

    const layerBlur = Number(styles.layerBlur) || 0;

    const parts = [
        hue ? `hue-rotate(${hue}deg)` : null,
        sat !== 100 ? `saturate(${sat}%)` : null,
        bri !== 100 ? `brightness(${bri}%)` : null,
        con !== 100 ? `contrast(${con}%)` : null,
        gray ? `grayscale(${gray}%)` : null,
        sepia ? `sepia(${sepia}%)` : null,
        invert ? `invert(${invert}%)` : null,
        // Layer blur — CSS `filter: blur()` blurs the element (and its overlays)
        // as one layer, exactly like Figma's Layer blur. Kept in this same filter
        // chain so DOM and the export's buildCanvasFilter apply it identically.
        layerBlur > 0 ? `blur(${layerBlur}px)` : null,
    ].filter(Boolean);

    return parts.length ? parts.join(' ') : undefined;
}

/**
 * CSS backdrop-filter for Background blur / Glass — blurs whatever is BEHIND the
 * element, seen through its transparent areas. Glass reuses the same blur (its
 * tint + highlight are painted as overlays on top). Returns undefined when off.
 */
export function buildBackdropFilter(styles = {}) {
    const blur = Math.max(Number(styles.backdropBlur) || 0, Number(styles.glassBlur) || 0);
    return blur > 0 ? `blur(${blur}px)` : undefined;
}

/* ------------------------------------------------------------------ *
 *  Effect activation — a single source of truth for "is effect X on?",
 *  shared by the panel (on-dot), the DOM overlay and the export bounds.
 * ------------------------------------------------------------------ */

export function isDropShadowOn(s = {}) { return getEffects(s, 'dropShadow').length > 0; }
export function isInnerShadowOn(s = {}) { return getEffects(s, 'innerShadow').length > 0; }
export function isLayerBlurOn(s = {}) { return (Number(s.layerBlur) || 0) > 0; }
export function isBackgroundBlurOn(s = {}) { return (Number(s.backdropBlur) || 0) > 0; }
export function isNoiseOn(s = {}) { return (Number(s.noiseAmount) || 0) > 0; }
export function isTextureOn(s = {}) {
    return !!s.textureType && s.textureType !== 'none' && (Number(s.textureOpacity) || 0) > 0;
}
export function isShaderOn(s = {}) {
    return !!s.shaderType && s.shaderType !== 'none' && (Number(s.shaderOpacity) || 0) > 0;
}
export function isGlassOn(s = {}) { return (Number(s.glassBlur) || 0) > 0; }

/** True when the element paints any content overlay (drawn on top of it). */
export function hasOverlayEffect(s = {}) {
    return isNoiseOn(s) || isTextureOn(s) || isShaderOn(s) || isGlassOn(s);
}

export const COLOR_ADJUST_DEFAULTS = {
    hueRotate: 0,
    saturate: 100,
    brightness: 100,
    contrast: 100,
    grayscale: 0,
    sepia: 0,
    invert: 0,
};

/**
 * Figma's blend menu order — darkening group, lightening group, contrast group,
 * comparative group, component group — each separated by a hairline. Figma's
 * "Plus darker" / "Plus lighter" are left out: `plus-darker` is Safari-only and
 * neither survives the raster/PDF export path, so offering them would let a
 * design look right on screen and wrong in print.
 */
/**
 * The angle a gradient takes until someone aims it: 250° — light falling from
 * the upper right, the direction most of the print templates already use. CSS
 * degrees, so 0 paints upwards and they run clockwise. Every surface (panel,
 * canvas gizmo, live paint, export) reads this one number, or they would
 * disagree about what an unset gradient looks like.
 */
export const GRADIENT_ANGLE_DEFAULT = 250;

export const BLEND_MODE_GROUPS = [
    [
        { value: 'normal', label: 'Normal' },
    ],
    [
        { value: 'darken', label: 'Darken' },
        { value: 'multiply', label: 'Multiply' },
        { value: 'color-burn', label: 'Color burn' },
    ],
    [
        { value: 'lighten', label: 'Lighten' },
        { value: 'screen', label: 'Screen' },
        { value: 'color-dodge', label: 'Color dodge' },
    ],
    [
        { value: 'overlay', label: 'Overlay' },
        { value: 'soft-light', label: 'Soft light' },
        { value: 'hard-light', label: 'Hard light' },
    ],
    [
        { value: 'difference', label: 'Difference' },
        { value: 'exclusion', label: 'Exclusion' },
    ],
    [
        { value: 'hue', label: 'Hue' },
        { value: 'saturation', label: 'Saturation' },
        { value: 'color', label: 'Color' },
        { value: 'luminosity', label: 'Luminosity' },
    ],
];

export const BLEND_MODE_OPTIONS = BLEND_MODE_GROUPS.flat();
